import { useAtomValue } from "jotai";
import { useMemo } from "react";
import { localeAtom } from "../store/atoms";
import { translationRegistry } from "../Registry";
import type { TranslationNamespace, TranslationParams } from "../store/types";
import type { CommonTranslations } from "../locales/en/Common";
import type { ControlPanelTranslations } from "../locales/en/ControlPanel";
import type { MicrospaceAnalysisTranslations } from "../locales/en/MicrospaceAnalysis";
import type { AssortmentSimulationTranslations } from "../locales/en/AssortmentSimulation";
import type { SpacePlanningTranslations } from "../locales/en/SpacePlanning";
import type { PlanogramsTranslations } from "../locales/en/Planograms";
import type { AssortmentSimulationDetailTranslations } from "../locales/en/AssortmentSimulationDetail";
import type { SettingsTranslations } from "../locales/en/Settings";
import type { PlanogramDeliveryTranslations } from "../locales/en/PlanogramDelivery";
import type { CdtStudioTranslations } from "../locales/en/CdtStudio";
import type { DistributionPlannerTranslations } from "../locales/en/DistributionPlanner";

type NamespaceToType = {
  common: CommonTranslations;
  controlPanel: ControlPanelTranslations;
  microspaceAnalysis: MicrospaceAnalysisTranslations;
  assortmentSimulation: AssortmentSimulationTranslations;
  spacePlanning: SpacePlanningTranslations;
  planograms: PlanogramsTranslations;
  assortmentSimulationDetail: AssortmentSimulationDetailTranslations;
  settings: SettingsTranslations;
  planogramDelivery: PlanogramDeliveryTranslations;
  cdtStudio: CdtStudioTranslations;
  distributionPlanner: DistributionPlannerTranslations;
};

type Prev = [never, 0, 1, 2, 3];

type Paths<T, D extends number = 4> = [D] extends [never]
  ? never
  : T extends object
    ? {
        [K in keyof T]-?: K extends string | number
          ? `${K}` | (Paths<T[K], Prev[D]> extends infer P extends string ? `${K}.${P}` : never)
          : never;
      }[keyof T]
    : never;

function getNestedValue(obj: Record<string, unknown>, path: string): string {
  const keys = path.split(".");
  let current: unknown = obj;

  for (const key of keys) {
    if (current === null || current === undefined || typeof current !== "object") {
      return path;
    }
    current = (current as Record<string, unknown>)[key];
  }

  return typeof current === "string" ? current : path;
}

/**
 * Params are inserted verbatim: translated strings are rendered as React text
 * nodes, which escape automatically, so HTML-escaping here only produced visible
 * entities (e.g. a variant named "WAFERS & ROLLS" rendered as "WAFERS &amp;
 * ROLLS"). The few call sites that render a translation as HTML sanitise it
 * themselves with DOMPurify.
 */
function interpolate(template: string, params?: TranslationParams): string {
  if (!params) return template;
  return template.replace(/\{\{(\w+)\}\}/g, (_, key: string) =>
    params[key] !== undefined ? String(params[key]) : `{{${key}}}`,
  );
}

export function useTranslation<N extends TranslationNamespace>(namespace: N) {
  const locale = useAtomValue(localeAtom);

  const dictionary = useMemo(() => translationRegistry[locale][namespace], [locale, namespace]);

  const t = useMemo(() => {
    return (key: Paths<NamespaceToType[N]>, params?: TranslationParams): string => {
      const raw = getNestedValue(dictionary as Record<string, unknown>, key);
      return interpolate(raw, params);
    };
  }, [dictionary]);

  const tDynamic = useMemo(() => {
    return (key: string, params?: TranslationParams): string => {
      const raw = getNestedValue(dictionary as Record<string, unknown>, key);
      return interpolate(raw, params);
    };
  }, [dictionary]);

  return { t, tDynamic, locale };
}
